import React, { useEffect, useState } from "react";
import { t } from "i18next";

import styles from "./TablePagination.module.scss";

// Compact data-grid style pagination: fixed width regardless of page count.
const TablePagination = ({
    page,
    totalPages,
    totalResults,
    limit,
    onPageChange,
    onLimitChange,
    pageSizeOptions = [10, 25, 50, 100],
}) => {
    const [pageInput, setPageInput] = useState(String(page));

    useEffect(() => {
        setPageInput(String(page));
    }, [page]);

    if (totalResults <= 0) return null;

    const from = (page - 1) * limit + 1;
    const to = Math.min(page * limit, totalResults);
    const isFirst = page <= 1;
    const isLast = page >= totalPages;

    const goTo = (target) => {
        const next = Math.min(Math.max(1, target), totalPages);
        if (next !== page) onPageChange(next);
        setPageInput(String(next));
    };

    const commitPageInput = () => {
        const value = parseInt(pageInput, 10);
        if (Number.isNaN(value)) {
            setPageInput(String(page));
        } else {
            goTo(value);
        }
    };

    const NavButton = ({ onClick, disabled, label, icon }) => (
        <button
            type="button"
            className={styles.navButton}
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            title={label}
        >
            <i className={`fa-solid ${icon}`} aria-hidden="true" />
        </button>
    );

    return (
        <div className={styles.bar} role="navigation" aria-label={t("page")}>
            <div className={styles.group}>
                <label htmlFor="table-page-size" className={styles.label}>
                    {t("rows_per_page")}
                </label>
                <select
                    id="table-page-size"
                    className={styles.select}
                    value={limit}
                    onChange={(e) => onLimitChange(Number(e.target.value))}
                >
                    {pageSizeOptions.map((size) => (
                        <option key={size} value={size}>
                            {size}
                        </option>
                    ))}
                </select>
            </div>

            <div className={styles.range} aria-live="polite">
                {from}&ndash;{to} {t("of")} {totalResults}
            </div>

            <div className={styles.group}>
                <NavButton onClick={() => goTo(1)} disabled={isFirst} label={t("first_page")} icon="fa-angles-left" />
                <NavButton onClick={() => goTo(page - 1)} disabled={isFirst} label={t("previous")} icon="fa-angle-left" />

                <span className={styles.pageJump}>
                    <span className={styles.label}>{t("page")}</span>
                    <input
                        type="number"
                        min={1}
                        max={totalPages}
                        inputMode="numeric"
                        className={styles.pageInput}
                        value={pageInput}
                        onChange={(e) => setPageInput(e.target.value)}
                        onBlur={commitPageInput}
                        onKeyDown={(e) => e.key === "Enter" && commitPageInput()}
                        aria-label={t("page")}
                    />
                    <span className={styles.label}>
                        {t("of")} {totalPages}
                    </span>
                </span>

                <NavButton onClick={() => goTo(page + 1)} disabled={isLast} label={t("next")} icon="fa-angle-right" />
                <NavButton onClick={() => goTo(totalPages)} disabled={isLast} label={t("last_page")} icon="fa-angles-right" />
            </div>
        </div>
    );
};

export default TablePagination;
